import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAllUsers, getAllUsersWithoutRestrictions, getEmployeesWithFilters, getActiveUsers, getActiveUsersWithDepartments, getBirthdays, checkExistingEmployee, updateExistingPassword
} from './thunk';
import { normalizeEntities, NormalizedState } from '../../utils/normalizeState';
import { Employee } from '../../types/api';

// Normalized state structure
export interface EmployeesState {
    employees: NormalizedState<Employee> & {
        pagination?: {
            total: number;
            page: number;
            per_page: number;
            total_pages: number;
        } | null;
    };
    birthdayData: any[];
    errors: Record<string, string | null>;
    loading: boolean;
}

export const initialState: EmployeesState = {
    employees: {
        entities: {},
        ids: [],
        loading: false,
        error: null,
        pagination: null,
    },
    birthdayData: [],
    errors: {},
    loading: false
};

const UsersSlice = createSlice({
    name: 'Employees',
    initialState,
    reducers: {
        // Clear employees state
        clearEmployees: (state) => {
            state.employees.entities = {};
            state.employees.ids = [];
            state.employees.error = null;
        },
        // Update single employee
        updateEmployee: (state, action: PayloadAction<Employee>) => {
            const employee = action.payload;
            if (state.employees.entities[employee.id]) {
                state.employees.entities[employee.id] = {
                    ...state.employees.entities[employee.id],
                    ...employee,
                };
            }
        },
    },
    extraReducers: (builder) => {
        // List - Normalized
        builder.addCase(getAllUsers.pending, (state) => {
            state.employees.loading = true;
            state.employees.error = null;
            state.loading = true;
        });
        builder.addCase(getAllUsers.fulfilled, (state, action: PayloadAction<Employee[]>) => {
            // Ensure payload is an array
            const payload = Array.isArray(action.payload) ? action.payload : [];
            const normalized = normalizeEntities(payload);
            state.employees.entities = normalized.entities;
            state.employees.ids = normalized.ids;
            state.employees.loading = false;
            state.employees.error = null;
            state.employees.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getAllUsers.rejected, (state, action) => {
            state.employees.error = action.payload as string || 'Failed to fetch employees';
            state.employees.loading = false;
            state.loading = false;
        });

        // Filtered List - Normalized
        builder.addCase(getEmployeesWithFilters.pending, (state) => {
            state.employees.loading = true;
            state.employees.error = null;
            state.loading = true;
        });
        builder.addCase(getEmployeesWithFilters.fulfilled, (state, action: PayloadAction<Employee[]>) => {
            // Ensure payload is an array
            const payload = Array.isArray(action.payload) ? action.payload : [];
            const normalized = normalizeEntities(payload);
            // Replace entities to ensure fresh data (don't merge to avoid stale role data)
            state.employees.entities = normalized.entities;
            state.employees.ids = normalized.ids;
            state.employees.loading = false;
            state.employees.error = null;
            state.employees.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getEmployeesWithFilters.rejected, (state, action) => {
            state.employees.error = action.payload as string || 'Failed to fetch filtered employees';
            state.employees.loading = false;
            state.loading = false;
        });

        // All Users Without Restrictions - Normalized
        builder.addCase(getAllUsersWithoutRestrictions.pending, (state) => {
            state.employees.loading = true;
            state.employees.error = null;
            state.loading = true;
        });
        builder.addCase(getAllUsersWithoutRestrictions.fulfilled, (state, action: PayloadAction<Employee[]>) => {
            // Ensure payload is an array
            const payload = Array.isArray(action.payload) ? action.payload : [];
            const normalized = normalizeEntities(payload);
            state.employees.entities = normalized.entities;
            state.employees.ids = normalized.ids;
            state.employees.loading = false;
            state.employees.error = null;
            state.employees.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getAllUsersWithoutRestrictions.rejected, (state, action) => {
            state.employees.error = action.payload as string || 'Failed to fetch all employees';
            state.employees.loading = false;
            state.loading = false;
        });

        // Active Users - Normalized
        builder.addCase(getActiveUsers.pending, (state) => {
            state.employees.loading = true;
            state.employees.error = null;
            state.loading = true;
        });
        builder.addCase(getActiveUsers.fulfilled, (state, action: PayloadAction<Employee[]>) => {
            // Ensure payload is an array
            const payload = Array.isArray(action.payload) ? action.payload : [];
            const normalized = normalizeEntities(payload);
            // Replace entities to ensure fresh data (don't merge to avoid stale role data)
            state.employees.entities = normalized.entities;
            state.employees.ids = normalized.ids;
            state.employees.loading = false;
            state.employees.error = null;
            state.employees.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getActiveUsers.rejected, (state, action) => {
            state.employees.error = action.payload as string || 'Failed to fetch active employees';
            state.employees.loading = false;
            state.loading = false;
        });

        // Active Users with Departments - Normalized
        builder.addCase(getActiveUsersWithDepartments.pending, (state) => {
            state.employees.loading = true;
            state.employees.error = null;
            state.loading = true;
        });
        builder.addCase(getActiveUsersWithDepartments.fulfilled, (state, action: PayloadAction<any>) => {
            // Handle both paginated and non-paginated responses
            let users: Employee[] = [];
            if (action.payload && typeof action.payload === 'object' && 'users' in action.payload) {
                // Paginated response
                users = Array.isArray(action.payload.users) ? action.payload.users : [];
                // Store pagination info in state if needed
                state.employees.pagination = action.payload.pagination || null;
            } else {
                // Non-paginated response (backward compatible)
                users = Array.isArray(action.payload) ? action.payload : [];
                state.employees.pagination = null;
            }
            
            const normalized = normalizeEntities(users);
            // Replace entities and ids to show only active users with departments
            state.employees.entities = normalized.entities;
            state.employees.ids = normalized.ids;
            state.employees.loading = false;
            state.employees.error = null;
            state.employees.lastUpdated = Date.now();
            state.loading = false;
        });
        builder.addCase(getActiveUsersWithDepartments.rejected, (state, action) => {
            state.employees.error = action.payload as string || 'Failed to fetch active employees with departments';
            state.employees.loading = false;
            state.loading = false;
        });
        builder.addCase(getBirthdays.fulfilled, (state, action) => {
            state.birthdayData = action.payload as any[];
        });
        builder.addCase(getBirthdays.rejected, (state, action) => {
            state.errors.birthdays = action.payload as string || 'Failed to fetch birthdays';
        });

        // Check Existing Employee
        builder.addCase(checkExistingEmployee.pending, (state) => {
            state.loading = true;
            state.errors.checkExisting = null;
        });
        builder.addCase(checkExistingEmployee.fulfilled, (state) => {
            state.loading = false;
            state.errors.checkExisting = null;
        });
        builder.addCase(checkExistingEmployee.rejected, (state, action) => {
            state.errors.checkExisting = action.payload as string || 'Failed to check existing employee';
            state.loading = false;
        });

        // Update Existing Password
        builder.addCase(updateExistingPassword.pending, (state) => {
            state.loading = true;
            state.errors.password = null;
        });
        builder.addCase(updateExistingPassword.fulfilled, (state) => {
            state.loading = false;
            state.errors.password = null;
        });
        builder.addCase(updateExistingPassword.rejected, (state, action) => {
            state.errors.password = action.payload as string || 'Failed to update password';
            state.loading = false;
        });

    }
});

export const { clearEmployees, updateEmployee } = UsersSlice.actions;

export default UsersSlice.reducer;